import { ApiService } from "./apiService";
import { API_URL_V4, API_URL_V2 } from "../config";
import axios from "axios";

const apiService = new ApiService();

// Add cancel token for chat agent requests
let cancelChatAgent;

// ✅ NEW: Multiple WebSocket connections (one per conversation)
let progressWebSockets = new Map();  // conversationId → WebSocket
let wsReconnectAttempts = new Map(); // conversationId → attemptCount
const MAX_RECONNECT_ATTEMPTS = 5;

export default class ChatAgentService {
  constructor() {
    this.progressCallbacks = new Map();  // conversationId → callback
  }
  // Send message with minimal context - backend handles data lookup
  sendMessage(message, context = {}, conversationId = null, categorySelection = null) {
    // Cancel any previous chat request
    if (cancelChatAgent !== undefined) {
      cancelChatAgent("cancel");
    }

    // Minimal context - only screen identifier, backend handles the rest
    const requestData = {
      message,
      context: {
        screen: context.screen || null,
        timestamp: new Date().toISOString(),
      },
      ...(conversationId && { conversation_id: conversationId }),
      ...(categorySelection && { category_selection: categorySelection }),
    };

    return axios
      .request({
        url: `${API_URL_V4}/chat-agent/message`,
        method: "POST",
        data: requestData,
        timeout: 300000,
        cancelToken: new axios.CancelToken(function executor(c) {
          cancelChatAgent = c;
        }),
      })
      .then((response) => {
        // Handle charts if present in response
        if (response.data.charts && response.data.charts.length > 0) {
          this.processCharts(response.data.charts);
        }
        
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        if (error.message === "cancel") {
          return Promise.reject({ message: "Request cancelled" });
        }
        
        // Handle timeout
        if (error.code === 'ECONNABORTED') {
          return Promise.reject({ 
            message: "Request timed out. The AI is processing complex data. Please try a simpler query." 
          });
        }
        
        // Handle service unavailable
        if (error.response?.status === 503) {
          return Promise.reject({ 
            message: "AI service is temporarily unavailable. Please try again in a moment." 
          });
        }
        
        // Handle gateway timeout
        if (error.response?.status === 504) {
          return Promise.reject({ 
            message: "The AI took too long to respond. Please try again with a simpler request." 
          });
        }
        
        // Default error
        return Promise.reject({ 
          message: error.response?.data?.message || "Failed to get response from AI agent" 
        });
      });
  }

  // NEW: Create conversation
  createConversation(initialMessage = "") {


    return axios
      .request({
        url: `${API_URL_V4}/chat-agent/conversations`,
        method: "POST",
        data: { initial_message: initialMessage },
        timeout: 10000
      })
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        console.error("❌ Create conversation failed:", error);
        
        // Provide fallback conversation generation
        const fallbackConversationId = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        
        return Promise.resolve({
          conversationId: fallbackConversationId,
          createdAt: new Date().toISOString(),
          fallback: true,
          message: "Conversation started locally"
        });
      });
  }

  // NEW: Get user conversations
  getUserConversations(limit = 50) {

    return axios
      .request({
        url: `${API_URL_V4}/chat-agent/conversations?limit=${limit}`,
        method: "GET",
        timeout: 15000
      })
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        console.error("❌ Load conversations failed:", error);
        
        // Handle different error cases
        if (error.response?.status === 401) {
          return Promise.reject({ 
            message: "Authentication required to load conversations" 
          });
        }
        
        if (error.response?.status === 403) {
          return Promise.reject({ 
            message: "Not authorized to access conversations" 
          });
        }
        
        // Default fallback
        return Promise.resolve({
          conversations: [],
          total: 0,
          user_email: "unknown",
          error: "Unable to load conversations from server"
        });
      });
  }

  // NEW: Get conversation details
  getConversationDetails(conversationId) {

    return axios
      .request({
        url: `${API_URL_V4}/chat-agent/conversations/${conversationId}`,
        method: "GET",
        timeout: 15000
      })
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        console.error("❌ Load conversation details failed:", error);
        
        if (error.response?.status === 404) {
          return Promise.reject({ 
            message: "Conversation not found" 
          });
        }
        
        return Promise.reject({ 
          message: error.response?.data?.message || "Failed to load conversation details" 
        });
      });
  }

  // NEW: Delete conversation
  deleteConversation(conversationId) {

    return axios
      .request({
        url: `${API_URL_V4}/chat-agent/conversations/${conversationId}`,
        method: "DELETE",
        timeout: 10000
      })
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        console.error("❌ Delete conversation failed:", error);
        
        if (error.response?.status === 404) {
          return Promise.reject({ 
            message: "Conversation not found" 
          });
        }
        
        return Promise.reject({ 
          message: error.response?.data?.message || "Failed to delete conversation" 
        });
      });
  }

  // NEW: Update conversation title
  updateConversationTitle(conversationId, title) {
    return axios
      .request({
        url: `${API_URL_V4}/chat-agent/conversations/${conversationId}`,
        method: "PATCH",
        data: { title },
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        console.error("❌ Update conversation title failed:", error);
        console.error("❌ Error details:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: error.config?.url,
          method: error.config?.method
        });
        
        if (error.response?.status === 404) {
          return Promise.reject({ 
            message: "Conversation not found" 
          });
        }
        
        if (error.response?.status === 405) {
          return Promise.reject({ 
            message: "Method not allowed - backend endpoint may not be deployed" 
          });
        }
        
        return Promise.reject({ 
          message: error.response?.data?.message || "Failed to update conversation title" 
        });
      });
  }

  // ✅ NEW: WebSocket methods for real-time progress updates (multi-conversation support)
  connectProgressWebSocket(conversationId, onProgress, onError) {
    // Check if already connected for this conversation
    const existingWs = progressWebSockets.get(conversationId);
    if (existingWs && existingWs.readyState === WebSocket.OPEN) {
      return existingWs;
    }
    
    // Close stale connection if exists
    if (existingWs) {
      existingWs.close();
      progressWebSockets.delete(conversationId);
    }
    
    // Store callback for this conversation
    this.progressCallbacks.set(conversationId, onProgress);
    
    // Determine WebSocket URL from API_URL_V4 (connects through C# proxy)
    const wsUrl = API_URL_V4.replace('https://', 'wss://').replace('http://', 'ws://');
    const wsEndpoint = `${wsUrl}/chat-agent/progress/${conversationId}`;
    
    // Get auth token for WebSocket connection
    const token = this.getAuthToken();
    const wsEndpointWithAuth = token ? `${wsEndpoint}?token=${encodeURIComponent(token)}` : wsEndpoint;
    
    try {
      const ws = new WebSocket(wsEndpointWithAuth);
      progressWebSockets.set(conversationId, ws);
      
      ws.onopen = () => {
        wsReconnectAttempts.set(conversationId, 0);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const callback = this.progressCallbacks.get(conversationId);
          
          if (callback) {
            // ✅ Always inject conversationId so reducer knows where to route updates
            callback({
              ...data,
              conversationId: conversationId
            });
          }
        } catch (error) {
          console.error(`❌ Error parsing WebSocket message for ${conversationId}:`, error);
        }
      };
      
      ws.onerror = (error) => {
        console.error(`❌ WebSocket error for ${conversationId}:`, error);
        if (onError) onError(error);
      };
      
      ws.onclose = (event) => {
        // Cleanup
        progressWebSockets.delete(conversationId);
        this.progressCallbacks.delete(conversationId);
        
        // Auto-reconnect if unexpected disconnect
        if (!event.wasClean) {
          const attempts = wsReconnectAttempts.get(conversationId) || 0;
          
          if (attempts < MAX_RECONNECT_ATTEMPTS) {
            const nextAttempt = attempts + 1;
            wsReconnectAttempts.set(conversationId, nextAttempt);
            
            const delay = 2000 * nextAttempt; // Exponential backoff
            
            setTimeout(() => {
              this.connectProgressWebSocket(conversationId, onProgress, onError);
            }, delay);
          } else {
            console.error(`❌ Max reconnection attempts reached for ${conversationId}`);
            wsReconnectAttempts.delete(conversationId);
          }
        } else {
          wsReconnectAttempts.delete(conversationId);
        }
      };
      
      return ws;
      
    } catch (error) {
      console.error(`❌ Failed to create WebSocket for ${conversationId}:`, error);
      if (onError) onError(error);
      return null;
    }
  }
  
  disconnectProgressWebSocket(conversationId = null) {
    if (!conversationId) {
      // No conversation ID provided = disconnect ALL
      progressWebSockets.forEach((ws, convId) => {
        ws.close();
      });
      progressWebSockets.clear();
      this.progressCallbacks.clear();
      wsReconnectAttempts.clear();
      return;
    }
    
    // Disconnect specific conversation
    const ws = progressWebSockets.get(conversationId);
    if (ws) {
      ws.close();
      progressWebSockets.delete(conversationId);
      this.progressCallbacks.delete(conversationId);
      wsReconnectAttempts.delete(conversationId);
    }
  }
  
  isWebSocketConnected(conversationId) {
    const ws = progressWebSockets.get(conversationId);
    return ws && ws.readyState === WebSocket.OPEN;
  }
  
  getActiveWebSocketCount() {
    return progressWebSockets.size;
  }
  
  getActiveConversationIds() {
    return Array.from(progressWebSockets.keys());
  }

  // Submit message feedback (thumbs up/down)
  submitMessageFeedback(messageId, feedbackType, feedbackText = null, conversationId = null) {
    const browserSessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    const requestData = {
      event_type: "feedback",
      conversation_id: conversationId,
      timestamp: new Date().toISOString(),
      browser_session_id: browserSessionId,
      data: {
        message_id: messageId,
        feedback_type: feedbackType,
        feedback_text: feedbackText || null,
      },
    };

    return axios
      .request({
        url: `${API_URL_V4}/events`,
        method: "POST",
        data: requestData,
        timeout: 10000,
      })
      .then((response) => {
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        console.error("❌ Submit feedback failed:", error);
        return Promise.reject({
          message: error.response?.data?.message || "Failed to submit feedback",
        });
      });
  }

  // NEW: Regenerate message response
  regenerateMessage(messageId, conversationId = null) {
    const requestData = {
      message_id: messageId,
      ...(conversationId && { conversation_id: conversationId }),
      timestamp: new Date().toISOString()
    };

    return axios
      .request({
        url: `${API_URL_V4}/chat-agent/regenerate`,
        method: "POST",
        data: requestData,
        timeout: 300000,
      })
      .then((response) => {
        // Handle charts if present in response
        if (response.data.charts && response.data.charts.length > 0) {
          this.processCharts(response.data.charts);
        }
        
        return Promise.resolve(response.data);
      })
      .catch((error) => {
        if (error.code === 'ECONNABORTED') {
          return Promise.reject({ 
            message: "Request timed out. The AI is processing complex data. Please try again." 
          });
        }
        
        if (error.response?.status === 503) {
          return Promise.reject({ 
            message: "AI service is temporarily unavailable. Please try again in a moment." 
          });
        }
        
        if (error.response?.status === 504) {
          return Promise.reject({ 
            message: "The AI took too long to respond. Please try again." 
          });
        }
        
        return Promise.reject({ 
          message: error.response?.data?.message || "Failed to regenerate response" 
        });
      });
  }

  // EXISTING: processCharts method (unchanged)
  processCharts(charts) {
    // Process HTML charts for rendering in UI
    charts.forEach(chart => {
      // Emit event for UI to handle chart rendering
      const event = new CustomEvent('agentChartReceived', {
        detail: {
          html: chart.html,
          type: chart.type,
          title: chart.title,
          data: chart.data
        }
      });
      window.dispatchEvent(event);
    });
  }

  // Helper: Get authentication token from localStorage or sessionStorage
  getAuthToken() {
    // Try to get token from various possible storage locations
    // Adjust based on your app's authentication implementation
    const token = localStorage.getItem('auth_token') || 
                  sessionStorage.getItem('auth_token') ||
                  localStorage.getItem('token') ||
                  sessionStorage.getItem('token');
    return token;
  }

  // NEW: Cancel all pending requests (utility method)
  cancelAllRequests() {
    if (cancelChatAgent !== undefined) {
      cancelChatAgent("cancel");
    }
  }
  
  async checkIfConversationIsEmpty(conversationId) {
  try {
    const response = await this.getConversationDetails(conversationId);
    return {
      isEmpty: response.message_count === 0,
      messageCount: response.message_count,
      exists: true
    };
  } catch (error) {
    return { 
      isEmpty: true, 
      messageCount: 0, 
      exists: false 
    };
  }
}

  // Get service status (utility method)
  async getServiceStatus() {
    try {
      const response = await axios.request({
        url: `${API_URL_V4}/chat-agent/status`,
        method: "GET",
        timeout: 5000
      });
      return response.data;
    } catch (error) {
      return { 
        status: "unavailable", 
        error: error.message 
      };
    }
  }

  // Get filter definitions for chat templates - uses existing insights API
  // viewType: 11 = Brand Performance, 6 = Market Share
  getFilterDefinitions(viewType = 11, dateRange = null) {
    const headers = {};
    if (dateRange) {
      headers.startDate = dateRange.startDate;
      headers.endDate = dateRange.endDate;
    }
    
    return axios
      .request({
        url: `${API_URL_V2}/insights/views/filterDefinitions/${viewType}`,
        method: "GET",
        headers: { ...headers, ...axios.defaults.headers },
        timeout: 15000
      })
      .then((response) => {
        // Response structure: { data: { filterDefinitions: [...], ... } }
        return Promise.resolve({
          filterDefinitions: response.data?.filterDefinitions || response.data || []
        });
      })
      .catch((error) => {
        console.error("❌ Load filter definitions failed:", error);
        return Promise.resolve({
          filterDefinitions: [],
          error: "Unable to load filter definitions from server"
        });
      });
  }

  // Get dynamic filter values (brands, keywords) for a specific retailer
  // Uses existing API: /insights/views/dynamicFilterDefinitions?retailer=xxx
  getDynamicFilterValues(retailer, categories = null, dateRange = null) {
    // Build query string
    let queryString = `retailer=${encodeURIComponent(retailer)}`;
    
    if (categories && categories.length > 0) {
      const categoryParams = categories
        .map(cat => `categories=${encodeURIComponent(cat)}`)
        .join('&');
      queryString += `&${categoryParams}`;
    }
    
    // Date range goes in headers
    const headers = {};
    if (dateRange) {
      headers.startDate = dateRange.startDate;
      headers.endDate = dateRange.endDate;
    }
    
    return axios
      .request({
        url: `${API_URL_V2}/insights/views/dynamicFilterDefinitions?${queryString}`,
        method: "GET",
        headers: { ...headers, ...axios.defaults.headers },
        timeout: 15000
      })
      .then((response) => {
        // Backend returns: { brands: [...], keywords: [...] }
        return Promise.resolve({
          brands: response.data?.brands || [],
          keywords: response.data?.keywords || []
        });
      })
      .catch((error) => {
        console.error("❌ Load dynamic filter values failed:", error);
        return Promise.resolve({
          brands: [],
          keywords: [],
          error: "Unable to load dynamic filters"
        });
      });
  }
}

// Export the cancel function like other services
export { cancelChatAgent };
